// Write-side shapes for the type/attribute/subtype builder (SEN-735). Reads reuse
// the .passthrough() TypeResponse/Attribute Zod in ./schemas; writes are plain
// interfaces sent to the generic types/records/attributes endpoints (mirrors the
// FilterExpr/ViewPatch convention in schemas.ts — built here, never parsed back).
import { z } from 'zod'

/** One kind offering — a row of GET /v1/tenants/{slug}/ontology `kinds` (the kind-picker source). */
export const Subtype = z.object({
  subtype_id: z.string(),
  tenant_id: z.string().nullable(),
  name: z.string(),
  base: z.string(),
  params: z.record(z.string(), z.unknown()).default({}),
  value_schema: z.record(z.string(), z.unknown()).nullish(),
  /** The kind's create-config contract, projected from its spec by the
   *  ontology: the config keys an attribute of this kind must be created with.
   *  Add attribute picks its config editor from these keys (SEN-316). */
  create_config: z.object({
    required: z.array(z.string()).default([]),
    optional_top_level: z.array(z.string()).default([]),
  }).nullish(),
}).passthrough()
export type Subtype = z.infer<typeof Subtype>
export const SubtypesResponse = z.array(Subtype)

/** The 8 fixed base families (monad/types_catalog/_bases.py BASES), canonical order.
 *  The ONLY frontend definition of this list — KindPicker imports it. */
export const BASES = ['string', 'number', 'boolean', 'datetime', 'closed-set', 'reference', 'composite', 'json'] as const

export interface TypeCreateBody {
  slug: string
  name: string
  singular_noun?: string
  plural_noun?: string
  icon?: string | null
  description?: string | null
  display?: Record<string, unknown> | null
  kenning_path?: string[]
  api_slug?: string | null
}

export interface TypeUpdateBody {
  name?: string
  singular_noun?: string | null
  plural_noun?: string | null
  icon?: string | null
  description?: string | null
  display?: Record<string, unknown> | null
  kenning_path?: string[]
  sort_order?: string | null
}

export interface AttributeCreateBody {
  slug: string
  name: string
  type: string
  config?: Record<string, unknown> | null
  display?: Record<string, unknown> | null
  title?: string | null
  api_slug?: string | null
  is_required?: boolean
  is_unique?: boolean
  is_multiselect?: boolean
  default_value?: Record<string, unknown> | null
  description?: string | null
  reverse_name?: string | null
  reverse_slug?: string | null
}

export interface AttributeUpdateBody {
  name?: string
  title?: string | null
  description?: string | null
  display?: Record<string, unknown> | null
  is_required?: boolean
  default_value?: Record<string, unknown> | null
  sort_order?: string | null
}
